/**
 * SoftSync brand logo — single source of truth for the mark + wordmark.
 *
 * Uses the real PNG logo (public/brand/softsync-logo.png) across all three
 * themes for visual consistency. Theme-specific tinting can be added later
 * via CSS filters if desired.
 *
 * Use <Logo /> for the mark alone (sidebar collapsed, favicon stand-in,
 * loading screens, 404/error pages). Use <LogoLockup /> for the mark plus
 * wordmark (sidebar expanded, auth-page header, dashboard hero).
 */

import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';

export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<LogoSize, number> = {
  sm: 20,
  md: 28,
  lg: 40,
  xl: 64,
};

export interface LogoProps {
  size?: LogoSize;
  className?: string;
}

export function Logo({ size = 'md', className }: LogoProps): JSX.Element {
  const px = SIZE_PX[size];
  return (
    <img
      src="/brand/softsync-logo.png"
      alt="SoftSync"
      width={px}
      height={px}
      className={cn('flex-shrink-0 select-none', className)}
      style={{ width: px, height: px }}
      draggable={false}
    />
  );
}

export interface LogoLockupProps {
  size?: LogoSize;
  className?: string;
  /** Hide the wordmark — useful in narrow rails. Equivalent to <Logo /> alone. */
  markOnly?: boolean;
  /** Override the small caption under the wordmark. Default: 'CRM'. Pass `null` to omit. */
  caption?: string | null;
}

export function LogoLockup({
  size = 'md',
  className,
  markOnly = false,
  caption = 'CRM',
}: LogoLockupProps): JSX.Element {
  const { theme } = useTheme();
  const wordSize =
    size === 'xl' ? 'text-xl' : size === 'lg' ? 'text-base' : size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <Logo size={size} />
      {!markOnly && (
        <div className="flex flex-col leading-none">
          <span
            className={cn(
              'font-semibold tracking-tight text-text',
              wordSize,
              theme === 'editorial' && 'font-display italic font-medium',
            )}
          >
            SoftSync
          </span>
          {caption !== null && caption !== '' && (
            <span className="mt-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-accent">
              {caption}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
